<template>
  <div class="w-full flex flex-col items-center justify-center">
    <div class="md:text-center space-y-3 max-w-lg md:max-w-xl">
      <BaseText
        type="display"
        size="2xs"
      >
        Enter Card Details to Activate Trial
      </BaseText>
      <BaseText
        type="body"
        size="md"
        class="text-neutral-alpha-650"
      >
        Your trial is 100% free of charge. Cancel at any time with one click.
      </BaseText>
    </div>
    <!--  -->
    <div class="max-w-lg md:max-w-4xl">
      <div class="h-full flex flex-col-reverse md:grid grid-cols-12 mt-7 md:mt-11 gap-x-7">
        <div class="col-span-6 space-y-4">
          <!-- Plan details -->
          <div class="hidden md:block bg-neutral-alpha-25 rounded-3xl p-5">
            <BaseText
              type="label"
              size="md"
              class="py-1 uppercase text-neutral-alpha-650"
            >
              {{ trialPlan.name }} Plan
            </BaseText>
            <div class="mt-4">
              <PlanFeatureList
                :plan-key="trialPlan.key"
                :is-monthly="true"
                :base-lens="trialPlan.lensCount"
              >
                <template #header>
                  <BaseText
                    type="label"
                    size="sm"
                  >
                    {{ trialPlan.includedUsers }} Free Users
                  </BaseText>
                </template>
              </PlanFeatureList>
              <div class="w-full pt-5">
                <button
                  class="group w-full flex items-center justify-center gap-1.5 p-2 bg-neutral-alpha-25 hover:bg-neutral-alpha-50 transition-colors duration-100"
                  style="border-radius: 10px;"
                  @click="$emit('back')"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 25 24"
                    fill="none"
                    class="text-neutral-alpha-800 transition-colors duration-100 group-hover:text-white"
                  >
                    <path
                      d="M13.8856 16L10.5927 12.7071C10.2022 12.3166 10.2022 11.6834 10.5927 11.2929L13.8856 8"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <BaseText
                    type="label"
                    size="sm"
                    class="text-neutral-alpha-800 transition-colors duration-100 group-hover:text-white"
                  >
                    Choose a different plan
                  </BaseText>
                </button>
              </div>
            </div>
          </div>
          <div class="bg-neutral-alpha-25 rounded-3xl p-5 space-y-5">
            <div class="space-y-3">
              <div
                v-for="guarantee in guaranteeDetails"
                :key="guarantee.title"
                class="flex items-start gap-3"
              >
                <component
                  :is="guarantee.icon?.default || guarantee.icon"
                  class="text-neutral-alpha-650 w-6 h-6"
                />
                <div class="space-y-1">
                  <BaseText
                    type="label"
                    size="md"
                    class="text-white"
                  >
                    {{ guarantee.title }}
                  </BaseText>
                  <BaseText
                    size="sm"
                    class="text-neutral-alpha-650"
                  >
                    {{ guarantee.desc }}
                  </BaseText>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- Checkout -->
        <div class="col-span-6">
          <div
            class="relative flex flex-col col-span-6 bg-white rounded-3xl p-3"
            style="height: max-content"
          >
            <div class="flex-grow flex flex-col mt-2">
              <div
                v-if="isCouponSet"
                class="p-2 flex items-center gap-1 w-full text-secondary-green-300 rounded-2xl relative"
                style="background-color: #effefa"
              >
                <CouponIcon class="w-5 h-5" />
                <div class="flex items-center gap-1">
                  <BaseText
                    type="label"
                    size="sm"
                  >
                    {{ couponInfo }}
                  </BaseText>
                </div>
                <button
                  class="absolute right-2 top-0 bottom-0 my-auto"
                  @click="couponData = null, isCouponSet = false, couponInput = ''"
                >
                  <TagRemoveIcon class="w-4 h-4" />
                </button>
              </div>
              <div class="p-2 grid grid-cols-12 gap-3">
                <StripeCheckout
                  ref="stripeCheckout"
                  :intent-secret="intentSecret"
                  :card-billing-address.sync="cardBillingAddress"
                  @statusChange="(status) => isFormValid = status"
                />
                <div class="col-span-12">
                  <div class="flex items-end gap-3">
                    <div class="flex-1 space-y-1">
                      <BaseText
                        type="label"
                        size="sm"
                        class="text-text-muted"
                      >
                        Coupon Code
                      </BaseText>
                      <input
                        v-model="couponInput"
                        placeholder="Add coupon code"
                        class="form-input fade-in min-w-0 w-full px-2 py-1.5"
                        @input="isCouponSet = false, couponData = null"
                      >
                    </div>
                    <button
                      class="base-btn px-3.5 py-1.5 h-8 flex items-center justify-center"
                      style="min-width: 50px"
                      @click="applyCoupon"
                    >
                      <BaseLoadingSpinnerCircle
                        v-if="loading.coupon"
                        class="text-text-muted"
                      />
                      <BaseText
                        v-else
                        size="sm"
                        class="text-text-muted"
                      >
                        Apply
                      </BaseText>
                    </button>
                  </div>
                </div>
              </div>
              <div class="w-full flex flex-col gap-3.5 mt-3">
                <button
                  class="submit-btn rounded-xl p-2 w-full flex items-center justify-center"
                  :disabled="!isFormValid"
                  @click="createTrialSubscription"
                >
                  <BaseLoadingSpinnerCircle
                    v-if="loading.payment"
                    class="text-white"
                  />
                  <BaseText
                    v-else
                    type="label"
                    size="md"
                  >
                    Pay $0 and Start your 7-Day Trial
                  </BaseText>
                </button>
                <div class="flex items-center gap-1 justify-center w-full">
                  <div
                    class="flex items-center border rounded-md px-2 py-1 pl-3"
                    style="border-color: #635bff"
                  >
                    <BaseText
                      type="label"
                      size="sm"
                      style="color: #635bff"
                    >
                      Powered by
                    </BaseText>
                    <div>
                      <img
                        src="../../../assets/images/stripe-logo.png"
                        alt=""
                        class="h-7"
                      >
                    </div>
                  </div>
                </div>
                <BaseText
                  type="body"
                  size="sm"
                  class="text-text-muted text-center"
                >
                  You will not be charged during your 7 day trial. End your subscription at any time with one click.
                </BaseText>
              </div>
            </div>
          </div>
          <div class="mt-3 p-4">
            <BaseText
              size="sm"
              class="text-white"
              style="font-size: 12px;"
            >
              By continuing, you agree to our
              <a
                href="https://www.foreplay.co/page/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                class="underline text-primary"
              >Terms of Service</a>
              and
              <a
                href="https://www.foreplay.co/page/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                class="underline text-primary"
              >Privacy Policy</a>.
            </BaseText>
          </div>
        </div>
      </div>
    </div>
    <!-- Choose Different Plan Btn (mobile) -->
    <button
      class="group w-full md:hidden flex items-center justify-center gap-0.5 py-2 pl-2 pr-4 transition-colors duration-100
    bg-neutral-alpha-25 hover:bg-neutral-alpha-50 mt-4 max-w-lg mb-16"
      style="border-radius: 10px;"
      @click="$emit('back')"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 25 24"
        fill="none"
        class="text-neutral-alpha-800 transition-colors duration-100 group-hover:text-neutral-alpha-800"
      >
        <path
          d="M13.8856 16L10.5927 12.7071C10.2022 12.3166 10.2022 11.6834 10.5927 11.2929L13.8856 8"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
      <BaseText
        type="label"
        size="sm"
        class="text-neutral-alpha-800 transition-colors duration-100 group-hover:text-neutral-alpha-800"
      >
        Choose a different plan
      </BaseText>
    </button>
  </div>
</template>

<script>
import posthog from 'posthog-js'
import foreplayAPI from '@/api/foreplayServer'
import { SUBSCRIPTION_PLANS, GUARANTEE_DETAILS } from '@/utils/stripe/productMeta'
import { getFingerprint } from '@/utils/fingerprint'
import { mapActions } from 'vuex'
// Components
import StripeCheckout from '@/components/account/subscription/StripeCheckout.vue'
import PlanFeatureList from '@/components/account/subscription/PlanFeatureList.vue'
// Icons
import CouponIcon from '@/components/globals/Icons/CouponIcon.vue'
import TagRemoveIcon from '@/components/globals/Icons/TagRemoveIcon.vue'
import MoneyCircleIcon from '@/components/globals/Icons/MoneyCircleIcon.vue'
export default {
  name: 'PaymentOnboardSection',
  components: {
    MoneyCircleIcon,
    TagRemoveIcon,
    CouponIcon,
    StripeCheckout,
    PlanFeatureList
  },
  props: {
    initPlan: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      // Statics
      guaranteeDetails: GUARANTEE_DETAILS,

      cardBillingAddress: {},

      // Monthly-only trials. Restore: this.initPlan.interval === 'month' ? 'monthly' : 'annual'
      currentInterval: 'monthly',
      couponInput: '',
      // Stripe
      isFormValid: false,
      intentSecret: '',
      paymentMethodId: '',
      couponData: null,
      isCouponSet: false,
      loading: {
        coupon: false,
        payment: false
      }
    }
  },
  computed: {
    couponInfo () {
      // Returns a string describing the sale/discount from couponData, or null if no valid coupon
      if (!this.couponData || this.couponData.valid === false) {
        return null
      }
      const coupon = this.couponData
      if (coupon.percent_off) {
        // e.g. "50% off for 3 months" or "50% off"
        let msg = `${coupon.percent_off}% off`
        if (coupon.duration === 'repeating' && coupon.duration_in_months) {
          msg += ` for ${coupon.duration_in_months} month${coupon.duration_in_months > 1 ? 's' : ''}`
        } else if (coupon.duration === 'once') {
          msg += ' - first payment only'
        } else if (coupon.duration === 'forever') {
          msg += ' - forever'
        }
        return msg
      }
      if (coupon.amount_off) {
        // e.g. "$10 off for 2 months"
        const currency = (coupon.currency || 'usd').toUpperCase()
        // Format amount (Stripe gives in cents for USD)
        let amount = coupon.amount_off
        if (currency === 'USD' || currency === 'CAD' || currency === 'AUD' || currency === 'NZD' || currency === 'SGD') {
          amount = (amount / 100).toFixed(2)
        }
        let msg = `$${amount} off`
        if (coupon.duration === 'repeating' && coupon.duration_in_months) {
          msg += ` for ${coupon.duration_in_months} month${coupon.duration_in_months > 1 ? 's' : ''}`
        } else if (coupon.duration === 'once') {
          msg += ' - first payment only'
        } else if (coupon.duration === 'forever') {
          msg += ' - forever'
        }
        return msg
      }
      if (coupon.type === 'trial') {
        return `${coupon.value} day${coupon.value > 1 ? 's' : ''} free trial`
      }
      return null
    },
    productReference () {
      const plan = SUBSCRIPTION_PLANS.find(plan => plan.key === this.initPlan.key)
      return {
        key: plan.key,
        name: plan.tier,
        description: plan.description,
        price: plan.price?.[this.currentInterval],
        interval: this.currentInterval,
        savingsPrice: plan.annualSavings,
        includedUsers: plan.baseUserCount,
        spyderCount: plan.baseSpyder,
        lensCount: plan.baseLens,
        includedProducts: plan.includedProducts,
        prices: plan.price,
        annualSavings: plan.annualSavings,
        priceId: plan.priceId,
        addOn: plan.addOns
      }
    },
    trialPlan () {
      if (this.currentInterval === this.initPlan.interval) {
        return this.initPlan
      }
      return this.productReference
    }
  },
  watch: {
    isFormValid (isValid, wasValid) {
      if (isValid && !wasValid) {
        posthog.capture('Payment Info Added', {
          plan: this.trialPlan.name,
          interval: this.currentInterval
        })
      }
    }
  },
  async mounted () {
    await this.initStripeIntent()
  },
  methods: {
    ...mapActions('AuthModule', ['fetchUserSubscription']),
    async initStripeIntent () {
      const res = await foreplayAPI.Subscription.createSetupIntent()
      const setupIntent = res.setupIntent
      this.intentSecret = setupIntent.client_secret
    },
    async createTrialSubscription () {
      if (this.loading.payment) return
      // Init Stripe Setup Intent
      this.loading.payment = true
      try {
        const result = await this.$refs.stripeCheckout.confirmSetup()

        if (result.error) {
          console.error('Payment method error:', result.error)
          this.loading.payment = false
          this.$showAlert({ type: 'error', message: result.error.message })
          return
        }

        console.log('Setup intent successful:', result.setupIntent)
        // payment method id is used on trial creation for automatic charging
        this.paymentMethodId = result.setupIntent.payment_method
      } catch (error) {
        this.$showAlert({ type: 'error', message: error.message })
        await this.initStripeIntent()
        return
      }
      console.log(this.trialPlan)

      // Create trial subscription
      try {
        const addOn = {
          lens: this.trialPlan?.addOn?.lens?.[this.currentInterval],
          spyder: this.trialPlan?.addOn?.spyder?.[this.currentInterval],
          api: this.trialPlan?.addOn?.api?.[this.currentInterval]
        }
        const dHash = await getFingerprint()
        const trialSubPayload = {
          priceId: this.trialPlan.priceId?.[this.currentInterval],
          addOn,
          paymentMethodId: this.paymentMethodId,
          dHash,
          ...(this.couponData && { couponId: this.couponData.id })
        }
        const { data } = await foreplayAPI.Subscription.createTrialSubscription(trialSubPayload)
        await this.fetchUserSubscription(true)

        // Update HubSpot fields
        try {
          await foreplayAPI.Users.updateHubspotFields({
            'onboard_payment_method_entered': true
          })
        } catch (error) {
          console.error('Failed to update HubSpot fields:', error)
        }

        // Emit next step
        this.$emit('next', {
          step: 'payment',
          data: {
            cardBillingAddress: this.cardBillingAddress
          }
        })
      } catch (error) {
        console.error(error)
        const errResponse = error.response?.data
        if (errResponse.error === 'subscription_exists') {
          this.$showAlert({ type: 'error', message: 'You already have a subscription. Redirecting to Discovery...' })
        } else {
          const errMessage = errResponse?.message
          const errCode = errResponse?.error || 'unknown_error'
          this.$showAlert({ type: 'error', message: errCode === 'trial_creation_failed' ? errMessage : 'Unable to create trial subscription. Please contact support.' })
        }
        await this.initStripeIntent()
        return
      } finally {
        this.loading.payment = false
      }
    },
    async createSetupIntent () {
      const { data } = await foreplayAPI.Subscription.createSetupIntent()
      console.log(data)
    },
    async applyCoupon () {
      this.loading.coupon = true
      try {
        const currentPriceId = this.initPlan.priceId?.[this.currentInterval]
        const res = await foreplayAPI.Subscription.applyCoupon(this.couponInput, currentPriceId)
        if (res?.coupon?.valid) {
          this.couponData = res.coupon
          this.isCouponSet = true
          this.$showAlert({ type: 'success', message: 'Coupon applied successfully' })
        } else {
          this.$showAlert({ type: 'error', message: res.message })
        }
      } catch (error) {
        const errCode = error.response?.data?.error
        if (errCode === 'PROMOTION_CODE_NOT_APPLICABLE_TO_PRODUCT') {
          this.$showAlert({ type: 'error', message: 'This coupon is not applicable to this product' })
        } else {
          this.$showAlert({ type: 'error', message: 'Invalid coupon code' })
        }
      } finally {
        this.loading.coupon = false
      }
    }
  }
}
</script>

<style scoped>
.money-icon{
  color: #EBBE7A
}

.submit-btn {
  background-color: #1F69FF;
  color: #fff;
  border-radius: 10px;
  box-shadow: none;
  transition: background-color 150ms ease-in-out, color 150ms ease-in-out, box-shadow 150ms ease-in-out;
}
.submit-btn:disabled {
  background-color:#F6F8FA;
  color: #A4acb9;
  box-shadow:none;
  cursor: not-allowed;
}

.base-btn {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13),
    0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
  border-radius: 6px;
  padding: 6px 8px 6px 8px;
}

.base-btn:hover {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.17),
    0px 0px 0px 1px rgba(0, 56, 108, 0.12);
}

.form-input {
  color: #06070F;
  border-radius: 6px;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  transition: box-shadow 150ms ease-in-out;
}
.form-input:focus {
  outline: none;
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.1), 0px 0px 0px 1px rgba(0, 56, 108, 0.1);
}
.form-input::placeholder {
  color: #B2B4C7;
  transition: color 150ms ease-in-out, opacity 150ms ease-in-out;
  opacity: 0.8;
}
.form-input:hover::placeholder {
  opacity: 1;
}
.form-input:focus::placeholder {
  color: #C1C7D0;
}

.form-input.invalid {
  box-shadow: none;
  border: 1px solid #FF002F;
}

.interval-toggle {
  background-color: #F9F9FA;
  border-radius: 10px;
  padding: 4px;
}
.interval-toggle .active-toggle {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px 0 rgba(36, 38, 47, 0.08), 0 0 0 1px rgba(36, 38, 47, 0.04);
  transition: box-shadow 150ms ease-in-out;
}
</style>
